import { Component, OnInit, EventEmitter, Output, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { GridOptions, ColDef, GridApi, SelectionColumnDef, RowSelectionOptions } from "ag-grid-community";
import { CheckboxRendererComponent } from "../../../common/components/ag-grid/checkbox-renderer.component";
import { Organization } from "../../../common/models/organization.model";
import { Address } from "../../../common/models/address.model";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { OrganizationService } from "../../../common/services/organization.service";
import { IBooleanFilterParams, AgGridBooleanFilterComponent } from '../../../common/components/ag-grid/filters/boolean-filter.component';
import { OnBehalfSteps } from "../../models/on-behalf.model";

import { deepClone } from "../../../builder/models/editor.model";

@Component({
    selector: 'on-behalf-organization-select',
    templateUrl: './on-behalf-organization-select.component.html',
    styleUrls: ['./on-behalf-organization-select.component.scss'],
    standalone: false
})
export class OnBehalfOrganizationSelectComponent implements OnInit, OnDestroy {

    private _defaultOrganization: Organization = { id: "", parentOrganizationId: "", isInternal: false, isLegacy: false, name: "", isActive: true, typeCode: "", address: { street: "", suite: "", poBox: "", city: "", stateCode: "", zipCode: "" } as Address } as Organization;
    private _newOrganization: Organization = { id: "new", parentOrganizationId: "", secretaryOfStateId: "", isInternal: false, isLegacy: false, name: "[New Organization]", isActive: true, typeCode: "", address: { street: "", suite: "", poBox: "", city: "", stateCode: "", zipCode: "" } as Address } as Organization;
    formGroup: FormGroup;
    filteredList: Organization[];
    gridOptions: GridOptions;
	gridApi?: GridApi;
    
    get selectionColumnDef(): SelectionColumnDef {
        return this.formatterService.defaultSelectionColumnDef;
    }

    @Output() nextStep: EventEmitter<OnBehalfSteps> = new EventEmitter<OnBehalfSteps>();
    newOrganization: boolean = false;
    noODNR: boolean = false;
    organizationList: Organization[];

    @Output() organizationSelected: EventEmitter<Organization> = new EventEmitter<Organization>();
    selectedOrganization?: Organization;

    get selectedHistoricalOwnerNumber(): string {
        if (this.selectedOrganization && this.selectedOrganization.historicalOwnerNumber) return this.selectedOrganization.historicalOwnerNumber;
        return "";
    }

    get selectedOrganizationId(): string {
        if (this.selectedOrganization) return this.selectedOrganization.id;
        return "";
    }

    get selectedOrganizationName(): string {
        if (this.selectedOrganization && this.selectedOrganization.name) return this.selectedOrganization.name;
        return "";
    }

    subscription: Subscription = new Subscription();

    constructor(private builder: FormBuilder,
        private formatterService: GridFormatterService,
        private organizationService: OrganizationService
	) { }

    ngOnInit() {
        this.selectedOrganization = this._defaultOrganization;
        this.buildForm();
        this.setGridOptions();
        let org$ = this.organizationService.getAllExternalOrganizations().pipe(tap((data: Organization[]) => {
            data = data.sort((a, b) => {
                if (a.name < b.name) return -1;
                if (b.name < a.name) return 1;
                if (a.id < b.id) return -1;
                if (b.id < a.id) return 1;
                return 0;
            });
            this.organizationList = data;
        })).subscribe({
            next: () => { console.log("organizations loaded"); },
            error: (error) => { console.error(error); }
         });
        this.subscription.add(org$);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    advance(): void {
        this.nextStep.emit(OnBehalfSteps.GenerateApplicationNumber);
    }

    applyFilters(): void {
        this.noODNR = false;
        this.filteredList = deepClone(this.organizationList);
        let name = this.formGroup.get("legalName");
        let rbdms = this.formGroup.get("rbdmsNumber");
        let rics = this.formGroup.get("ricsNumber");       
        if (rics?.value !== "") {
            this.filteredList = this.filteredList.filter(f => f.id.toString() === rics.value.toString());
        } else if (rbdms?.value !== "") {
            this.filteredList = this.filteredList.filter(f => (f.id.toString() === rbdms.value.toString() && !f.isLegacy) || (f.historicalOwnerNumber != null && f.historicalOwnerNumber.toString() === rbdms.value.toString()));
        } else if (name?.value !== "") {
            this.filteredList = this.filteredList.filter(f => f.name != null && f.name.toLocaleLowerCase().indexOf(name.value.toString().toLocaleLowerCase()) !== -1);
        }
        if (this.filteredList.length > 0) {
            this.filteredList = this.filteredList.sort((a, b) => {
                if (a.name < b.name) return -1; 
                if (b.name < a.name) return 1;
                if (a.id < b.id) return -1;
                if (b.id < a.id) return 1;
                return 0;
            });
        }  
    }

    buildForm(): void {
        this.formGroup = this.builder.group({
            ricsNumber: [this.selectedOrganizationId, []],
            rbdmsNumber: [this.selectedHistoricalOwnerNumber, []],
            legalName: [this.selectedOrganizationName, []],
            organization: [this.selectedOrganizationId, [Validators.required]],
            newOrg: [ this.newOrganization, []]
        });
    }

    defineGridColumns(): ColDef[] {
        const columns = [
            {                
                field: "id",
                checkboxSelection: false,
                headerName: "RICS Owner #",
                headerTooltip: "RICS Owner #",           
                sortable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
                tooltipField: "id",
                maxWidth: 175,
                resizable: true               
            } as ColDef, {
                field: "historicalOwnerNumber",
                headerCheckboxSelection: false,
                headerName: "RBDMS #",
                sortable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
                maxWidth: 175,
                resizable: true
            } as ColDef, {
                field: "hasRegistered",
                headerCheckboxSelection: false,
                headerName: "Registered in RICS ?",                
                tooltipValueGetter: this.formatterService.formatBooleanToYesNo("hasRegistered"),
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "hasRegistered",
                    description: "hasRegistered",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
                sortable: true,
                filter: AgGridBooleanFilterComponent,
                filterParams: {
                    isInServerSideFilterMode: false,
                } as IBooleanFilterParams,
                maxWidth: 150,
                resizable: true
            },
            {
                field: "name",
                headerCheckboxSelection: false,
                headerName: "Organization's Legal Name",
                headerTooltip: "Organization's Legal Name",
                tooltipField: "name",
                sortable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
                resizable: true
            }

        ] as ColDef[];
        this.formatterService.applyFilterKeyboardHintTooltips(columns);
        return columns;
    }

    noMatches() {
        this.newOrganization = !this.newOrganization;
        if (this.gridApi) {
            this.gridApi.deselectAll();
        }        
        this.organizationChanged(null);
    }

    organizationChanged(data: Event): void {        
        const tempSelected = [];
        if (this.gridApi) {
            const rows = this.gridApi.getSelectedRows();
            if (rows.length > 0) {
                this.formGroup.get("newOrg").patchValue(false);
            }
            for (let r = 0; r < rows.length; r++) {
                tempSelected.push(rows[r] as Organization);
            }
        }       
        if (tempSelected.length > 0) {
            this.newOrganization = false;
            this.selectedOrganization = tempSelected[0];            
        } else if (this.newOrganization) {
            let newOrg = this.formGroup.get("newOrg");
            this.selectedOrganization = (this.newOrganization) ? deepClone(this._newOrganization) : deepClone(this._defaultOrganization);
            newOrg.updateValueAndValidity();
        } else {
            this.selectedOrganization = deepClone(this._defaultOrganization);
        }
        if (this.selectedOrganization.id === "0002") {
            this.noODNR = true;
            this.selectedOrganization = deepClone(this._defaultOrganization);
            this.updateOrganization(false);
        } else {
            this.updateOrganization(true);
        }           
    }

    resetSearch(): void {
        this.selectedOrganization = undefined;
        this.filteredList = undefined;
        this.formGroup.get("organization").patchValue("");
        this.formGroup.get("rbdmsNumber").patchValue("");
        this.formGroup.get("ricsNumber").patchValue("");
        this.formGroup.get("legalName").patchValue("");
        this.noODNR = false;
        this.buildForm();
    }

    searchNames(): void {
        this.filteredList = [];
        let name = this.formGroup.get("legalName");
        let rbdms = this.formGroup.get("rbdmsNumber");
        let rics = this.formGroup.get("ricsNumber");
        let org = this.formGroup.get("organization");
        this.selectedOrganization = undefined;
        this.organizationSelected.emit(this._defaultOrganization);
        name.updateValueAndValidity();
        rbdms.patchValue("");
        rics.patchValue("");
        org.patchValue("");
        this.applyFilters();
    }

    searchRBDMS(): void {
        this.filteredList = [];
        let name = this.formGroup.get("legalName");
        let rbdms = this.formGroup.get("rbdmsNumber");
        let rics = this.formGroup.get("ricsNumber");
        let org = this.formGroup.get("organization");
        this.selectedOrganization = undefined;
        this.organizationSelected.emit(this._defaultOrganization);
        rbdms.updateValueAndValidity();
        rics.patchValue("");
        name.patchValue("");
        org.patchValue("");
        this.applyFilters();
    }

    searchRICS(): void {
        this.filteredList = [];
        let name = this.formGroup.get("legalName");
        let rbdms = this.formGroup.get("rbdmsNumber");
        let rics = this.formGroup.get("ricsNumber");
        let org = this.formGroup.get("organization");
        this.selectedOrganization = undefined;
        this.organizationSelected.emit(this._defaultOrganization);
        rics.updateValueAndValidity();
        rbdms.patchValue("");
        name.patchValue("");
        org.patchValue("");
        this.applyFilters();
    }

    private setGridOptions(): void {
        this.gridOptions = {
            columnDefs: this.defineGridColumns(),
            defaultColDef: { filter: true, sortable: true, headerClass: "wrapped" } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            headerHeight: 50,
			onGridReady: (params) => {
				this.gridApi = params.api;
                this.gridApi.sizeColumnsToFit();
            },
            rowSelection: {
                mode: "singleRow"
            } as RowSelectionOptions,
            suppressHorizontalScroll: true,
            pagination: true,
            paginationAutoPageSize: true
        } as GridOptions;
    }

    updateOrganization(doEmit: boolean): void {
        let orgCtrl = this.formGroup.get("organization");
        orgCtrl.patchValue(this.selectedOrganizationId);
        orgCtrl.updateValueAndValidity()
        if (doEmit) {
            this.organizationSelected.emit(this.selectedOrganization);
        }
    }

}
